import {
    Box,
    Button,
    Divider,
    EmptyState,
    Heading,
    LinkText,
    Report,
    Status,
    StatusBadge,
    Success,
    Table,
    TextBlock,
    TextCell,
    Upload,
} from "@bcc-ui/ui-components";
import { t } from "i18next";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { CellProps, Column } from "react-table";

import style from "./risk-factors-table.scss";
import { LockState } from "components/asset-management-system/assetRiskUtils";
import { ErasureCertificateAsset } from "components/asset-management-system/custom-views/tabs/risk/cards/risk-factors-card/erasureCertificates";
import {
    LockDetail,
    LockedDeviceAsset,
} from "components/asset-management-system/custom-views/tabs/risk/cards/risk-factors-card/lockedDevices";
import { UploadCustomReportDialog } from "components/asset-management-system/UploadCustomReportDialog";
import ErasureReportModal from "components/reports/ErasureReportModal";
import {
    CANCELLED_ERASURE,
    DIAGNOSED_ERASURE,
    FAILED_ERASURE,
    SUCCESSFUL_ERASURE,
    UNKNOWN_ERASURE,
} from "domain/asset-management/assets";
import { fileSharingService } from "services/file-sharing/FileSharingService";
import { createTranslatorPath } from "services/pathTranslator";
import { logger } from "utils/logging";

export type RiskFactorDataType = "ERASURE_CERTIFICATE" | "LOCKED_DEVICES";

interface ReportToView {
    reportUuid: string;
    date: string;
}

interface Props {
    dataType: RiskFactorDataType;
    erasureCertificates: ErasureCertificateAsset[];
    lockedDevices: LockedDeviceAsset[];
    isLoading: boolean;
    onErasureReportUploaded: () => void;
}

export const RiskFactorsTable = (props: Props) => {
    const translatedPaths = createTranslatorPath(useTranslation, "assetPaths");

    const [assetToUploadFor, setAssetToUploadFor] = useState<ErasureCertificateAsset>();
    const [uploading, setUploading] = useState(false);
    const [reportToView, setReportToView] = useState<ReportToView>();

    const getStatusBadgeByErasureState = (erasureState?: string) => {
        switch (erasureState) {
            case SUCCESSFUL_ERASURE:
                return <StatusBadge value={t("Common.state.successful")} status={Status.SUCCESS} />;
            case FAILED_ERASURE:
                return <StatusBadge value={t("Common.state.failed")} status={Status.ERROR} />;
            case CANCELLED_ERASURE:
                return <StatusBadge value={t("Common.state.cancelled")} status={Status.WARNING} />;
            case DIAGNOSED_ERASURE:
                return <StatusBadge value={t("Common.state.diagnosed")} status={Status.WARNING} />;
            case UNKNOWN_ERASURE:
                return <StatusBadge value={t("Common.state.unknown")} status={Status.WARNING} />;
            default:
                return <StatusBadge value={erasureState ?? t("Common.dataNotAvailable")} status={Status.NEUTRAL} />;
        }
    };

    const handleViewReport = async (asset: ErasureCertificateAsset) => {
        if (asset.reportUuid === undefined) {
            return;
        }
        if (asset.reportS3Link !== undefined) {
            try {
                const { presigned_url } = await fileSharingService.getDownloadUrl(asset.reportUuid);
                window.open(presigned_url, "_blank");
            } catch (error) {
                logger.error("Failed to fetch download URL for custom erasure report.", error);
            }
            return;
        }
        setReportToView({ reportUuid: asset.reportUuid, date: asset.erasureDate ?? "" });
    };

    const getStatusBadgeByLockState = (lockState: LockState, label?: string) => {
        switch (lockState) {
            case LockState.LOCKED:
                return (
                    <StatusBadge
                        value={label ?? t("AssetManagement.risk.riskOverviewTable.locked")}
                        status={Status.ERROR}
                        className={style.lockStatusError}
                    />
                );
            case LockState.UNLOCKED:
                return (
                    <StatusBadge
                        value={label ?? t("AssetManagement.risk.riskOverviewTable.unlocked")}
                        status={Status.SUCCESS}
                        className={style.lockStatusSuccess}
                    />
                );
            case LockState.NOT_AVAILABLE:
                return <StatusBadge value={label ?? t("Common.dataNotAvailable")} status={Status.NEUTRAL} />;
            default:
                return;
        }
    };

    const erasureCertificateColumns: Array<Column<ErasureCertificateAsset>> = [
        {
            Header: t("AssetManagement.risk.riskOverviewTable.tableHeader.deviceId"),
            accessor: "id",
            minWidth: 100,
            width: 150,
            Cell: ({ value }: CellProps<ErasureCertificateAsset>) => {
                return (
                    <TextCell>
                        <LinkText
                            as="button"
                            onClick={() => {
                                // TODO BCC-6837: Add click action
                            }}
                        >
                            {value}
                        </LinkText>
                    </TextCell>
                );
            },
        },
        {
            Header: t("Common.status"),
            accessor: "erasureState",
            minWidth: 100,
            width: 400,
            Cell: ({ value }: CellProps<ErasureCertificateAsset>) => {
                return (
                    <TextCell>
                        <Box gap={"Xxs"} verticalAlignment="Center">
                            {getStatusBadgeByErasureState(value)}
                        </Box>
                    </TextCell>
                );
            },
        },
        {
            Header: t("AssetManagement.risk.riskOverviewTable.tableHeader.actions"),
            minWidth: 100,
            width: 400,
            className: style.actionsCell,
            Cell: ({ row }: CellProps<ErasureCertificateAsset>) => {
                return (
                    <Box gap={"Xs"}>
                        <Button
                            variant={"Tertiary"}
                            icon={<Upload />}
                            onClick={() => setAssetToUploadFor(row.original)}
                        >
                            {t("Common.upload")}
                        </Button>

                        <Divider />

                        <Button
                            variant={"Tertiary"}
                            icon={<Report />}
                            disabled={row.original.reportUuid === undefined}
                            onClick={() => void handleViewReport(row.original)}
                        >
                            {t("Common.view")}
                        </Button>
                    </Box>
                );
            },
        },
    ];

    const lockedDevicesColumns: Array<Column<LockedDeviceAsset>> = [
        {
            Header: t("AssetManagement.risk.riskOverviewTable.tableHeader.deviceId"),
            accessor: "id",
            minWidth: 100,
            width: 150,
            Cell: ({ value }: CellProps<LockedDeviceAsset>) => {
                return (
                    <TextCell>
                        <LinkText
                            as="button"
                            onClick={() => {
                                // TODO BCC-6837: Add click action
                            }}
                        >
                            {value}
                        </LinkText>
                    </TextCell>
                );
            },
        },
        {
            Header: t("AssetManagement.risk.riskOverviewTable.tableHeader.lockType"),
            accessor: "lockDetails",
            id: "lockType",
            minWidth: 100,
            width: 400,
            Cell: ({ value }: CellProps<LockedDeviceAsset, LockDetail[]>) => {
                const lockTypes = value.map((detail) => translatedPaths(detail.lockPath)).join(", ");
                return <TextCell>{lockTypes === "" ? t("Common.dataNotAvailable") : lockTypes}</TextCell>;
            },
        },
        {
            Header: t("AssetManagement.risk.riskOverviewTable.tableHeader.lockStatus"),
            accessor: "lockDetails",
            id: "lockStatus",
            minWidth: 100,
            Cell: ({ value }: CellProps<LockedDeviceAsset, LockDetail[]>) => {
                if (!value || value.length === 0) {
                    return (
                        <TextCell>
                            <Box verticalAlignment="Center">
                                <StatusBadge value={t("Common.dataNotAvailable")} status={Status.NEUTRAL} />
                            </Box>
                        </TextCell>
                    );
                }
                return (
                    <TextCell>
                        <Box columnLayout gap="Xxxs">
                            {value.map((detail) => {
                                const label = t(
                                    `AssetManagement.risk.riskOverviewTable.lockLabel.${detail.lockPath}.${detail.rawValue.toUpperCase()}`,
                                    { defaultValue: detail.rawValue }
                                );
                                return (
                                    <Box key={detail.lockPath} verticalAlignment="Center">
                                        {getStatusBadgeByLockState(detail.lockState, label)}
                                    </Box>
                                );
                            })}
                        </Box>
                    </TextCell>
                );
            },
        },
    ];

    const sharedTableProps = {
        tableIdentity: "riskFactorsCardTable",
        className: style.table,
        emptyStateMessage: (
            <EmptyState
                hideIcon
                text={
                    <Box columnLayout horizontalAlignment="Center" verticalAlignment="Center" gap={"Sm"}>
                        <Success size={48} />
                        <Box columnLayout horizontalAlignment="Center" verticalAlignment="Center" gap={"Xxxs"}>
                            <Heading variant={"h2"}>
                                {t("AssetManagement.overview.riskLevelSummaryCards.noRiskIssues")}
                            </Heading>
                            <TextBlock alignCenter disableBottomSpacing className={style.secondaryText}>
                                {t("AssetManagement.overview.riskLevelSummaryCards.nothingToReview")}
                            </TextBlock>
                        </Box>
                    </Box>
                }
            />
        ),
    };

    if (props.dataType === "LOCKED_DEVICES") {
        return (
            <Table
                key={props.dataType}
                {...sharedTableProps}
                data={props.lockedDevices}
                columns={lockedDevicesColumns}
                loaded={!props.isLoading}
            />
        );
    }

    return (
        <>
            <Table
                key={props.dataType}
                {...sharedTableProps}
                data={props.erasureCertificates}
                columns={erasureCertificateColumns}
                loaded={!props.isLoading}
            />
            {assetToUploadFor !== undefined && (
                <UploadCustomReportDialog
                    isOpen
                    assetUuid={assetToUploadFor.uuid}
                    assetIdentifier={assetToUploadFor.id}
                    onClose={() => setAssetToUploadFor(undefined)}
                    onUploadComplete={() => {
                        setAssetToUploadFor(undefined);
                        props.onErasureReportUploaded();
                    }}
                    setUploading={setUploading}
                    uploading={uploading}
                />
            )}
            {reportToView !== undefined && (
                <ErasureReportModal
                    reportUuid={reportToView.reportUuid}
                    date={reportToView.date}
                    hide={() => setReportToView(undefined)}
                    openInNewTab={() => {}}
                    onReportDelete={() => {}}
                />
            )}
        </>
    );
};
